import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator, Modal, ScrollView, StyleSheet, Text, TextInput,
  TouchableOpacity, View,
} from 'react-native';
import { Category, Equipment, Store, StoreInventory, StoreOrderItem } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import CrossPlatformAlert from './CrossPlatformAlert';

interface Props {
  visible: boolean;
  store: Store;
  categories: Category[];
  equipment: Equipment[];
  inventory: StoreInventory[];
  onClose: () => void;
  onSave: (items: StoreOrderItem[], notes: string) => Promise<void>;
}

function descendantIds(categoryId: string, categories: Category[]): string[] {
  const result = [categoryId];
  for (const child of categories.filter(c => c.parentId === categoryId)) {
    result.push(...descendantIds(child.id, categories));
  }
  return result;
}

export default function StoreOrderWizard({
  visible, store, categories, equipment, inventory, onClose, onSave,
}: Props) {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const roots = useMemo(() => categories.filter(c => !c.parentId), [categories]);
  const selected = roots.filter(c => selectedCategoryIds.includes(c.id));
  const reviewStep = selected.length + 1;
  const currentCategory = step > 0 && step < reviewStep ? selected[step - 1] : null;
  const currentEquipment = currentCategory
    ? equipment
      .filter(eq => descendantIds(currentCategory.id, categories).includes(eq.categoryId))
      .sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const chosenEquipment = equipment
    .filter(eq => (quantities[eq.id] ?? 0) > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (visible) {
      setStep(0);
      setSelectedCategoryIds([]);
      setQuantities({});
      setNotes('');
      setSaving(false);
    }
  }, [visible]);

  const stock = (equipmentId: string) =>
    inventory.find(i => i.equipmentId === equipmentId)?.quantity ?? 0;

  const setQuantity = (equipmentId: string, raw: string) => {
    const cleaned = raw.replace(/[^0-9]/g, '');
    setQuantities(prev => ({ ...prev, [equipmentId]: parseInt(cleaned, 10) || 0 }));
  };

  const adjust = (equipmentId: string, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [equipmentId]: Math.max(0, (prev[equipmentId] ?? 0) + delta),
    }));
  };

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds(prev => {
      if (!prev.includes(id)) return [...prev, id];
      const categoryIds = descendantIds(id, categories);
      const equipmentIds = new Set(
        equipment.filter(eq => categoryIds.includes(eq.categoryId)).map(eq => eq.id),
      );
      setQuantities(current => Object.fromEntries(
        Object.entries(current).filter(([equipmentId]) => !equipmentIds.has(equipmentId)),
      ));
      return prev.filter(value => value !== id);
    });
  };

  const save = async () => {
    if (chosenEquipment.length === 0) {
      CrossPlatformAlert.alert(t('common.validation'), t('stores.orderEmpty'));
      return;
    }
    const items: StoreOrderItem[] = chosenEquipment.map(eq => {
      const category = categories.find(c => c.id === eq.categoryId);
      return {
        equipmentId: eq.id,
        equipmentName: eq.name,
        categoryId: eq.categoryId,
        categoryName: category?.name ?? '',
        unit: eq.unit,
        currentQuantity: stock(eq.id),
        orderedQuantity: quantities[eq.id],
      };
    });
    setSaving(true);
    try {
      await onSave(items, notes);
      onClose();
    } catch (error: any) {
      CrossPlatformAlert.alert(t('common.error'), error.message);
    } finally {
      setSaving(false);
    }
  };

  const title = step === 0
    ? t('stores.orderCategories')
    : step === reviewStep
      ? t('stores.orderSummary')
      : `${t('stores.orderEquipment')}: ${currentCategory?.name ?? ''}`;

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={styles.container}>
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.store}>{store.name}</Text>
          </View>
          <TouchableOpacity onPress={onClose}><Text style={styles.close}>✕</Text></TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.content}>
          {step === 0 && (
            <>
              <Text style={styles.hint}>{t('stores.orderSelectCategories')}</Text>
              {roots.map(category => {
                const active = selectedCategoryIds.includes(category.id);
                return (
                  <TouchableOpacity
                    key={category.id}
                    style={[styles.category, active && styles.categoryActive]}
                    onPress={() => toggleCategory(category.id)}
                  >
                    <Text style={[styles.categoryText, active && styles.categoryTextActive]}>
                      {active ? '✓ ' : ''}{category.name}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </>
          )}

          {currentCategory && (
            <>
              {currentEquipment.length === 0 && <Text style={styles.hint}>{t('stores.orderNoEquipment')}</Text>}
              {currentEquipment.map(eq => (
                <View key={eq.id} style={styles.itemRow}>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{eq.name}</Text>
                    <Text style={styles.stock}>
                      {t('stores.orderCurrentStock').replace('%s', `${stock(eq.id)} ${eq.unit}`)}
                    </Text>
                  </View>
                  <TouchableOpacity style={styles.qtyButton} onPress={() => adjust(eq.id, -1)}>
                    <Text style={styles.qtyButtonText}>−</Text>
                  </TouchableOpacity>
                  <TextInput
                    style={styles.qtyInput}
                    value={String(quantities[eq.id] ?? 0)}
                    onChangeText={value => setQuantity(eq.id, value)}
                    keyboardType="number-pad"
                    selectTextOnFocus
                  />
                  <TouchableOpacity style={styles.qtyButton} onPress={() => adjust(eq.id, 1)}>
                    <Text style={styles.qtyButtonText}>+</Text>
                  </TouchableOpacity>
                  <Text style={styles.unit}>{eq.unit}</Text>
                </View>
              ))}
            </>
          )}

          {step === reviewStep && (
            <>
              {chosenEquipment.length === 0 ? (
                <Text style={styles.hint}>{t('stores.orderEmpty')}</Text>
              ) : chosenEquipment.map(eq => (
                <View key={eq.id} style={styles.summaryRow}>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{eq.name}</Text>
                    <Text style={styles.stock}>
                      {t('stores.orderCurrentStock').replace('%s', `${stock(eq.id)} ${eq.unit}`)}
                    </Text>
                  </View>
                  <Text style={styles.summaryQty}>{quantities[eq.id]} {eq.unit}</Text>
                </View>
              ))}
              <Text style={styles.notesLabel}>{t('stores.orderNotes')}</Text>
              <TextInput
                style={styles.notes}
                value={notes}
                onChangeText={setNotes}
                placeholder={t('stores.orderNotesPlaceholder')}
                multiline
              />
            </>
          )}
        </ScrollView>

        <View style={styles.footer}>
          {step > 0 && (
            <TouchableOpacity style={styles.secondary} onPress={() => setStep(value => value - 1)}>
              <Text style={styles.secondaryText}>{t('usage.wizardBack')}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.primary, (saving || (step === 0 && selected.length === 0)) && styles.disabled]}
            disabled={saving || (step === 0 && selected.length === 0)}
            onPress={step === reviewStep ? save : () => setStep(value => value + 1)}
          >
            {saving ? <ActivityIndicator color="#FFF" /> : (
              <Text style={styles.primaryText}>
                {step === reviewStep ? t('stores.orderConfirm') : t('usage.wizardNext')}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  header: { padding: 18, backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0', flexDirection: 'row', justifyContent: 'space-between' },
  title: { fontSize: 20, fontWeight: '700', color: '#1E293B' },
  store: { marginTop: 3, color: '#64748B' },
  close: { fontSize: 22, color: '#64748B', padding: 4 },
  content: { padding: 16, maxWidth: 760, width: '100%', alignSelf: 'center' },
  hint: { color: '#64748B', marginBottom: 14 },
  category: { backgroundColor: '#FFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 10, padding: 16, marginBottom: 10 },
  categoryActive: { backgroundColor: '#EFF6FF', borderColor: '#2563EB' },
  categoryText: { color: '#334155', fontWeight: '600' },
  categoryTextActive: { color: '#2563EB' },
  itemRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  itemInfo: { flex: 1 },
  itemName: { color: '#1E293B', fontWeight: '600' },
  stock: { color: '#94A3B8', fontSize: 12, marginTop: 3 },
  qtyButton: { width: 34, height: 34, borderRadius: 7, backgroundColor: '#E2E8F0', alignItems: 'center', justifyContent: 'center' },
  qtyButtonText: { fontSize: 20, color: '#334155' },
  qtyInput: { width: 54, height: 36, marginHorizontal: 6, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 7, textAlign: 'center', backgroundColor: '#FFF' },
  unit: { width: 42, marginLeft: 6, color: '#64748B' },
  summaryRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  summaryQty: { color: '#2563EB', fontWeight: '700' },
  notesLabel: { marginTop: 22, marginBottom: 8, fontWeight: '600', color: '#334155' },
  notes: { minHeight: 90, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, padding: 12, backgroundColor: '#FFF', textAlignVertical: 'top' },
  footer: { padding: 16, borderTopWidth: 1, borderTopColor: '#E2E8F0', backgroundColor: '#FFF', flexDirection: 'row', justifyContent: 'flex-end', gap: 10 },
  secondary: { paddingHorizontal: 20, paddingVertical: 12, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8 },
  secondaryText: { color: '#475569', fontWeight: '600' },
  primary: { minWidth: 110, paddingHorizontal: 20, paddingVertical: 12, backgroundColor: '#2563EB', borderRadius: 8, alignItems: 'center' },
  primaryText: { color: '#FFF', fontWeight: '700' },
  disabled: { opacity: 0.5 },
});
